import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useGoogleAuthStatus, GOOGLE_AUTH_STATUS_QUERY_KEY } from '@/hooks/useGoogleAuthStatus';
import { useReauth } from '@/contexts/ReauthContext';

// Persistent top banner shown on every page as long as one of the user's Google
// accounts is not usable. Sibling of AccessRevokedBanner, but one level higher: there a
// single Merchant Center lost access, here the whole OAuth connection is unusable.
//
// Without this the breakage was invisible: the connection badge in Settings is
// derived from token_expires_at (a local clock, never Google's real state), and
// the only sync error lived in the tab that started it.
//
// Two variants, two different repair instructions (Finding R2-3):
//
//   reauth_required → the grant existed and Google killed it, usually after the user
//                     hit "Remove access" in their Google account.
//   scope_missing   → consent completed, but the Merchant API checkbox stayed unticked.
//                     Nothing was ever revoked here, so pointing this user at
//                     myaccount.google.com would send them looking for a permission
//                     they never gave. The fix is a checkbox on the NEXT consent screen.
//
// The wording of the scope variant mirrors the pre-consent interstitial on the
// marketing site (web#64), so the user reads the same instruction before and after.

const REVOKED_VARIANT = {
  testId: 'google-auth-revoked-banner',
  headlineKey: 'googleAuthRevoked.banner.revokedFor',
  emailFallbackKey: 'googleAuthRevoked.banner.emailFallback',
  noteKey: 'googleAuthRevoked.banner.disabledNote',
  causeKey: 'googleAuthRevoked.banner.cause',
  ctaKey: 'googleAuthRevoked.banner.reconnect',
  shortKey: 'googleAuthRevoked.banner.short',
  ctaShortKey: 'googleAuthRevoked.banner.ctaShort',
  // Selects the copy of the dialog behind the CTA. Without this the scope-missing user
  // is told the connection expired and that they "just need to click through Google's
  // authorization screen", which is the exact behaviour that produced the missing scope.
  reason: 'expired',
} as const;

const SCOPE_MISSING_VARIANT = {
  testId: 'google-scope-missing-banner',
  headlineKey: 'googleScopeMissing.banner.missingFor',
  emailFallbackKey: 'googleScopeMissing.banner.emailFallback',
  noteKey: 'googleScopeMissing.banner.disabledNote',
  causeKey: 'googleScopeMissing.banner.tickTheBox',
  ctaKey: 'googleScopeMissing.banner.grantPermission',
  shortKey: 'googleScopeMissing.banner.short',
  ctaShortKey: 'googleScopeMissing.banner.ctaShort',
  reason: 'scope_missing',
} as const;

export function GoogleAuthRevokedBanner() {
  const { t } = useTranslation('common');
  const {
    hasRevokedAccount,
    primaryRevokedAccount,
    hasScopeMissingAccount,
    primaryScopeMissingAccount,
  } = useGoogleAuthStatus();
  const { triggerReauth } = useReauth();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  // Exactly one banner at a time. A revoked grant wins: it is the state where even the
  // sign-in identity is gone, and its reconnect flow re-runs consent anyway, which is
  // also where a missing scope would get fixed.
  const revoked = hasRevokedAccount && primaryRevokedAccount ? primaryRevokedAccount : null;
  const scopeMissing =
    !revoked && hasScopeMissingAccount && primaryScopeMissingAccount
      ? primaryScopeMissingAccount
      : null;

  const account = revoked ?? scopeMissing;
  const variant = revoked ? REVOKED_VARIANT : SCOPE_MISSING_VARIANT;

  if (!account) return null;

  const displayEmail = account.email || t(variant.emailFallbackKey);

  const handleReconnect = () => {
    triggerReauth(
      {
        googleAccountId: account.id,
        googleAccountEmail: account.email,
        reason: variant.reason,
      },
      // google-oauth-handler heals auth_status in the same write as the fresh tokens,
      // but this component only hears about it through the realtime socket. If that
      // socket is down (staleTime 30s, refetchOnWindowFocus off, Layout never remounts)
      // the red banner would sit on top of a working connection for the rest of the
      // session. Refetching here makes the healing independent of the websocket.
      () => {
        queryClient.invalidateQueries({ queryKey: [GOOGLE_AUTH_STATUS_QUERY_KEY] });
      },
    );
  };

  const headline = (
    <Trans
      i18nKey={variant.headlineKey}
      t={t}
      values={{ email: displayEmail }}
      components={{ bold: <strong /> }}
    />
  );

  return (
    // role="alert": this is a destructive message that disables sync, optimization and
    // upload. role="status" is the polite live region and would be queued behind
    // whatever the screen reader is currently saying.
    // Not sticky itself — Layout wraps the banner stack in one sticky container so two
    // active banners sit under each other instead of overlapping at top: 0.
    <div
      role="alert"
      data-testid={variant.testId}
      className="border-b border-destructive/30 bg-destructive/10 px-4 py-1 text-sm text-destructive-ink backdrop-blur sm:py-2 print:hidden"
    >
      {/*
        G3-04, Platzbilanz unter sm (Plan WP6 Task 9): eine Zeile aus Symbol
        (16 + 8 px), Kurzsatz als Details-Schalter (min-h-11, truncate) und genau
        EINEM Knopf h-11 rechts. Zeile 44 + py-1 8 + Rand 1 = 53 px je Banner,
        beide Banner zusammen 106 px (vorher 226 bis 266 px bei 390 px).
        Kurzsatz-Platz bei 390/360/320 px: rund 196/166/126 px. Der volle Text
        steht aufgeklappt UNTER der Zeile, nie inline: ein Inline-Schalter bricht
        die Zeile wieder um.
      */}
      <div data-testid="banner-mobile-row" className="flex min-h-11 items-center gap-2 sm:hidden">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <button
          type="button"
          data-testid="banner-toggle"
          className="flex min-h-11 min-w-0 flex-1 items-center gap-1 text-left font-semibold"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          <span className="truncate">{t(variant.shortKey)}</span>
          <ChevronDown
            className={`h-4 w-4 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
          <span className="sr-only">{expanded ? t('banner.hideDetails') : t('banner.showDetails')}</span>
        </button>
        <Button type="button" size="sm" className="h-11 flex-shrink-0 px-3 text-xs" onClick={handleReconnect}>
          {t(variant.ctaShortKey)}
        </Button>
      </div>
      {expanded && (
        <div data-testid="banner-details" className="pb-2 pl-6 sm:hidden">
          {headline} {t(variant.noteKey)}{' '}
          <span className="text-destructive-ink/80">{t(variant.causeKey)}</span>
        </div>
      )}

      <div
        data-testid="banner-desktop"
        className="mx-auto hidden max-w-screen-2xl gap-2 sm:flex sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            {headline}{' '}
            {t(variant.noteKey)}{' '}
            <span className="text-destructive-ink/80">{t(variant.causeKey)}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" size="sm" className="h-7 text-xs" onClick={handleReconnect}>
            {t(variant.ctaKey)}
          </Button>
        </div>
      </div>
    </div>
  );
}
